import React, { useRef, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { Input } from '@factorialco/f0-react/dist/experimental'
import Pin from '@factorialco/f0-react/icons/app/Pin'
import debounce from 'lodash/debounce'
import tzlookup from 'tz-lookup'

import { GoogleMapsContext } from 'modules/core/components/GoogleMaps/GoogleMapsProvider'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'

import { castError } from 'modules/expenses/components/expenses/Detail/_newForm/castError'
import { WithCustomizationFieldsProps } from 'modules/expenses/components/expenses/Detail/_newForm/withCustomizationFields'
import { WithFieldPermissionsProps } from 'modules/expenses/components/expenses/Detail/_newForm/withFieldPermissions'

interface Props extends WithCustomizationFieldsProps, WithFieldPermissionsProps {
  fieldName: string
  label: string
  placeholder: string
  onPlaceSelected?: (place: { lat: number; lng: number }) => void
  onInputChange?: () => void
}

type PlaceAttributes = {
  address: string
  postcode: string
  state: string
  city: string
  country: string
  timeZone: string
  lat?: number
  lng?: number
  siret?: string
}

type Suggestion = {
  label: string
  value: string
}

const useLocationSuggestions = ({
  placesServiceRef,
  placesLibraryLoaded
}: {
  placesServiceRef: React.MutableRefObject<HTMLDivElement | null>
  placesLibraryLoaded: boolean
}) => {
  const [debouncedValue, setDebouncedValue] = useState<string>('')
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const {
    access: { locale: currentLocale }
  } = useCurrentSession()
  const locale = currentLocale.split('-')[0] ?? 'en'

  const debouncedOnSearch = useMemo(
    () =>
      debounce((val: string) => {
        setDebouncedValue(val)
      }, 500),
    []
  )

  useEffect(() => {
    return () => {
      debouncedOnSearch.cancel()
    }
    // debouncedOnSearch is stable (useMemo with []), so [] is safe here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!placesServiceRef.current || typeof google === 'undefined' || !placesLibraryLoaded) return

    if (debouncedValue.length === 0) {
      setSuggestions([])
      return
    }

    new google.maps.places.AutocompleteService().getPlacePredictions(
      { input: debouncedValue, language: locale },
      (placeSuggestions) => {
        setSuggestions(
          placeSuggestions?.map((suggestion) => ({
            label: suggestion.description,
            value: suggestion.place_id
          })) || []
        )
      }
    )
  }, [debouncedValue, placesLibraryLoaded, locale])

  const onSearchChange = (value: string) => {
    debouncedOnSearch(value)
  }

  const clearSuggestions = () => {
    setSuggestions([])
  }

  return {
    suggestions,
    clearSuggestions,
    onSearchChange
  }
}

const getPlaceAttributes = async (
  place: google.maps.places.PlaceResult
): Promise<PlaceAttributes> => {
  let address = place.name || ''
  let postcode = ''
  let city = ''
  let state = ''
  let country = ''
  const siret = ''
  const lat = place.geometry?.location?.lat()
  const lng = place.geometry?.location?.lng()

  if (!place.address_components) {
    return { address, postcode, state, city, country, lat, lng, timeZone: '', siret }
  }

  for (const component of place.address_components ?? []) {
    const componentType = component.types[0]

    switch (componentType) {
      case 'street_number': {
        break
      }

      case 'route': {
        address += `, ${component.short_name}`
        break
      }

      case 'postal_code': {
        postcode = `${component.long_name}${postcode}`
        break
      }

      case 'postal_code_suffix': {
        postcode = `${postcode}-${component.long_name}`
        break
      }

      case 'locality':
        address += `, ${component.short_name}`
        city = component.long_name
        break

      case 'administrative_area_level_1': {
        state = component.short_name
        break
      }

      case 'country':
        address += `, ${component.short_name}`
        country = component.short_name.toLowerCase()
        break
    }
  }

  const timeZone = lat && lng ? tzlookup(lat, lng) : ''

  return { address, postcode, state, city, country, lat, lng, timeZone, siret }
}

const LocationInput = ({
  isFieldMandatory,
  isFieldReadOnly,
  fieldName,
  label,
  placeholder,
  onPlaceSelected,
  onInputChange
}: Props) => {
  const {
    control,
    setValue,
    formState: { errors }
  } = useFormContext()

  const { placesLibraryLoaded } = React.useContext(GoogleMapsContext)

  // Hidden div used as the required DOM element for PlacesService
  const placesServiceRef = useRef<HTMLDivElement | null>(null)
  const { suggestions, clearSuggestions, onSearchChange } = useLocationSuggestions({
    placesServiceRef,
    placesLibraryLoaded
  })

  const [activeIndex, setActiveIndex] = useState(-1)

  const onLocationSelected = (placeId: string): void => {
    if (!placesServiceRef.current) {
      return
    }

    if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
      return
    }

    const request = { placeId, fields: ['geometry', 'address_components', 'name'] }

    const placeService = new google.maps.places.PlacesService(placesServiceRef.current)

    placeService.getDetails(request, (result, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && result) {
        getPlaceAttributes(result)
          .then((placeAttributes) => {
            onPlaceSelected?.({
              lat: placeAttributes.lat ?? 0,
              lng: placeAttributes.lng ?? 0
            })
          })
          .catch(() => {
            // Silently fail - Google Places API errors should not crash the form
          })
      } else {
        // PlacesService returned a non-OK status, silently fail
      }
    })
  }

  return (
    <>
      <div ref={placesServiceRef} style={{ display: 'none' }} />
      <Controller
        control={control}
        name={fieldName}
        render={({ field }) => {
          const handleSelect = (result: { value: string; label: string }) => {
            setValue(fieldName, result.label.trim(), { shouldDirty: true, shouldValidate: true })
            onLocationSelected(result.value)
            clearSuggestions()
            setActiveIndex(-1)
          }

          const handleChange = (value: string) => {
            setValue(fieldName, value, { shouldDirty: true, shouldValidate: true })
            onSearchChange(value)
            setActiveIndex(-1)
            // Notify parent that the user is typing free text (not selecting a suggestion),
            // so stale coordinates from a previous selection can be cleared.
            onInputChange?.()
          }

          const handleKeyDown = (e: React.KeyboardEvent) => {
            if (suggestions.length === 0) return

            switch (e.key) {
              case 'ArrowDown':
                e.preventDefault()
                setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0))
                break
              case 'ArrowUp':
                e.preventDefault()
                setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1))
                break
              case 'Enter':
                e.preventDefault()
                if (activeIndex >= 0 && activeIndex < suggestions.length) {
                  const selected = suggestions[activeIndex]
                  if (selected) {
                    handleSelect(selected)
                  }
                }
                break
              case 'Escape':
                e.preventDefault()
                clearSuggestions()
                setActiveIndex(-1)
                break
            }
          }

          const listboxId = `${fieldName}-listbox`
          const getOptionId = (index: number) => `${fieldName}-option-${index}`

          return (
            <div
              style={{ position: 'relative' }}
              role="combobox"
              aria-expanded={suggestions.length > 0}
              aria-haspopup="listbox"
              aria-owns={listboxId}
              onKeyDown={handleKeyDown}
            >
              <Input
                label={label}
                value={field.value ?? ''}
                onChange={handleChange}
                placeholder={placeholder}
                required={isFieldMandatory}
                disabled={isFieldReadOnly}
                error={castError(errors?.[fieldName])}
                icon={Pin}
              />
              {suggestions.length > 0 && (
                <ul
                  id={listboxId}
                  role="listbox"
                  aria-label={label}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    background: 'var(--color-surface-default, #fff)',
                    border: '1px solid var(--color-border-default, #e0e0e0)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    listStyle: 'none',
                    margin: '4px 0 0',
                    padding: '4px 0',
                    maxHeight: '240px',
                    overflowY: 'auto'
                  }}
                >
                  {suggestions.map((suggestion, index) => (
                    <li
                      key={suggestion.value}
                      id={getOptionId(index)}
                      role="option"
                      aria-selected={index === activeIndex}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        background:
                          index === activeIndex
                            ? 'var(--color-surface-hover, #f0f0f0)'
                            : 'transparent'
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        handleSelect(suggestion)
                      }}
                      onMouseEnter={() => setActiveIndex(index)}
                    >
                      {suggestion.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )
        }}
      />
    </>
  )
}

export default LocationInput
